/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { ReactNode } from 'react';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { OrderSourceTag } from './OrderSourceTag';

dayjs.extend(utc);
dayjs.extend(timezone);

export type NoticeboardCardColor =
  | 'yellow'
  | 'orange'
  | 'green'
  | 'blue'
  | 'gray';

const headerBgClasses: Record<NoticeboardCardColor, string> = {
  yellow: 'bg-amber-50',
  orange: 'bg-orange-50',
  green: 'bg-emerald-50',
  blue: 'bg-sky-50',
  gray: 'bg-gray-50',
};

const headerBorderClasses: Record<NoticeboardCardColor, string> = {
  yellow: 'border-l-4 border-l-amber-400',
  orange: 'border-l-4 border-l-orange-400',
  green: 'border-l-4 border-l-emerald-400',
  blue: 'border-l-4 border-l-sky-400',
  gray: 'border-l-4 border-l-gray-300',
};

export interface NoticeboardCardField {
  label: string;
  value: string | ReactNode;
}

export interface NoticeboardCardProps {
  status: string;
  referenceNumber: string;
  source?: string | null;
  color?: NoticeboardCardColor;
  fields: NoticeboardCardField[];
  dateLabel?: string;
  dateValue?: string;
  notes?: string;
  hasIssues?: boolean;
  onClick?: () => void;
  onArchive?: () => void;
}

export function NoticeboardCard({
  status,
  referenceNumber,
  source,
  color = 'gray',
  fields,
  dateLabel,
  dateValue,
  notes,
  hasIssues,
  onClick,
  onArchive,
}: NoticeboardCardProps) {
  return (
    <div
      onClick={onClick}
      className='w-full bg-white rounded-lg shadow-sm border border-gray-200 text-left hover:shadow-md transition-shadow cursor-pointer overflow-hidden'
    >
      {/* Colored Header with left accent */}
      <div
        className={`px-3 py-2.5 ${headerBgClasses[color]} ${headerBorderClasses[color]} flex items-start justify-between`}
      >
        <div className='flex items-start gap-1.5 min-w-0'>
          {hasIssues && (
            <ExclamationCircleIcon className='w-5 h-5 text-amber-500 flex-shrink-0' />
          )}
          <div className='min-w-0'>
            <div className='flex items-center gap-1.5'>
              <p className='text-xs font-semibold text-gray-900 leading-tight'>
                {status}
              </p>
              <OrderSourceTag source={source} />
            </div>
            <p className='text-xs text-primary font-medium mt-0.5 truncate'>
              {referenceNumber}
            </p>
          </div>
        </div>
        {onArchive && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onArchive();
            }}
            className='p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded transition-colors -mr-1 -mt-0.5'
            title='Archive'
          >
            <ArchiveBoxIcon className='w-4 h-4' />
          </button>
        )}
      </div>

      {/* Card Body */}
      <div className='px-3 py-2.5'>
        {/* Fields */}
        <div className='space-y-1'>
          {fields.map((field) => (
            <div
              key={field.label}
              className='flex justify-between text-xs gap-2'
            >
              <span className='text-gray-500 shrink-0'>{field.label}</span>
              <span className='text-gray-900 text-right truncate'>
                {field.value}
              </span>
            </div>
          ))}
        </div>

        {/* Date row */}
        {dateLabel && dateValue && (
          <div className='flex justify-between text-xs mt-2.5 pt-2 border-t border-gray-100'>
            <span className='text-gray-500'>{dateLabel}</span>
            <span className='text-gray-900'>{dateValue}</span>
          </div>
        )}

        {/* Notes */}
        {notes && (
          <div className='mt-2 pt-2 border-t border-gray-100'>
            <p className='text-xs text-gray-400'>Notes:</p>
            <p className='text-xs text-gray-500 mt-0.5'>{notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
